import { useState } from 'react';
import {
  Alert,
  Button,
  CancelIcon,
  Chip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Spinner,
  Text,
  Textarea,
} from '@billda/design-system';
import { LabelValueRow } from '../LabelValueRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import { useCancelAdminDeduction } from '../../hooks/useCancelAdminDeduction';

export interface CancelDeductionDialogDeduction {
  id: string;
  uniqueReference: string | null;
  description: string;
  amount: number;
  workerOrGangName?: string | null;
  status: string;
}

const statusChipConfig: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  Approved: { color: 'success', label: 'Approved' },
  Booked: { color: 'warning', label: 'Booked' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

export function CancelDeductionDialog({
  open,
  onClose,
  deduction,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  deduction: CancelDeductionDialogDeduction;
  onSuccess: (message: string) => void;
}) {
  const [notes, setNotes] = useState('');

  const { mutate: cancelDeduction, isPending } = useCancelAdminDeduction();

  const notesTooShort = notes.trim().length > 0 && notes.trim().length < 10;

  const handleConfirm = () => {
    cancelDeduction(
      {
        deductionId: deduction.id,
        request: {
          cancellationNotes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => onSuccess('Deduction cancelled successfully'),
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          handleClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>Cancel Deduction</DialogHeader>
        <DialogBody>
          <div className="mt-2 flex flex-col gap-5">
            <div className="rounded-lg bg-surface-raised p-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Text variant="body2" color="secondary">
                    {deduction.uniqueReference ?? '—'}
                  </Text>
                  <Chip
                    label={
                      statusChipConfig[deduction.status]?.label ??
                      deduction.status
                    }
                    color={
                      statusChipConfig[deduction.status]?.color ?? 'default'
                    }
                    size="sm"
                    className="font-semibold"
                  />
                </div>
                <Text variant="body2" className="font-semibold">
                  {deduction.description}
                </Text>
                <LabelValueRow
                  label="Worker / Gang"
                  value={deduction.workerOrGangName ?? '—'}
                />
                <LabelValueRow
                  label="Amount"
                  value={formatCurrency(deduction.amount)}
                />
              </div>
            </div>

            <Field
              label="Cancellation Notes (optional)"
              error={
                notesTooShort ? 'Min 10 characters if provided' : undefined
              }
            >
              <Textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any notes about this cancellation..."
                invalid={notesTooShort}
                data-testid="cancel-deduction-notes"
              />
            </Field>

            <Alert severity="warning">
              Cancelling this deduction will update pot financial totals. This
              action cannot be undone.
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={handleClose} variant="outline" disabled={isPending}>
            Go Back
          </Button>
          <Button
            onClick={handleConfirm}
            variant="danger"
            disabled={isPending || notesTooShort}
            data-testid="confirm-cancel-deduction-button"
          >
            {isPending ? (
              <Spinner size={20} color="inherit" />
            ) : (
              <CancelIcon aria-hidden="true" className="size-5" />
            )}
            {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
